import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IEditAutoDebitConfigRequest {
  id: string;
  autoDebitFrequency?: string;
  amount?: number;
  dayOfWeek?: string;
  dayOfMonth?: number | string;
  isEnabled?: boolean;
  startDate?: string;
  endDate?: string;
  pin?: string;
}

interface IEditAutoDebitConfigResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IEditAutoDebitConfigResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const editAutoDebitConfigAsync = createAsyncThunk(
  'savings/autoDebit/edit',
  async (
    { id, ...payload }: IEditAutoDebitConfigRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.patch<IEditAutoDebitConfigResponse>(
        `/savings/auto-debit-configurations/${id}`,
        payload,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IEditAutoDebitConfigResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const editAutoDebitConfigSlice = createSlice({
  name: 'savings/autoDebit/edit',
  initialState,
  reducers: {
    resetEditAutoDebitConfig(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editAutoDebitConfigAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(editAutoDebitConfigAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IEditAutoDebitConfigResponse;
      })
      .addCase(editAutoDebitConfigAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IEditAutoDebitConfigResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetEditAutoDebitConfig } = editAutoDebitConfigSlice.actions;

export default editAutoDebitConfigSlice.reducer;
